import { RefObject, useEffect } from 'react';

import { Dayjs } from 'dayjs';

import {
  FINALIZE_RETRY_MS,
  HORIZONTAL_PAGINATION_THRESHOLD_DAYS,
  HORIZONTAL_SWIPE_MIN_DELTA_PX,
  HORIZONTAL_WINDOW_SHIFT_DAYS,
  LEFT_EXPAND_COOLDOWN_MS,
  LOAD_MORE_ROWS_THRESHOLD_PX,
  NON_WHEEL_RELEASE_GRACE_MS,
  PLANNER_PAGINATION_LOG_TAG,
  RIGHT_EXPAND_COOLDOWN_MS,
  SCROLL_IDLE_DEBOUNCE_MS,
  WHEEL_COOLDOWN_MS,
  WHEEL_THROTTLE_MS,
} from '@/modules/planner';
import { logger } from '@/utils/logger/logger';

import {
  DateRelation,
  getDateRelationToLoadedRange,
} from '../../helpers/planner-base/planner-pagination-helpers';
import {
  clampDateIndex,
  getDefaultLeftLoadTriggerPx,
  getDefaultRightLoadTriggerPx,
  getViewportIndices,
} from '../../helpers/scroll/planner-scroll';
import usePlannerStore from '../../store';

interface UsePlannerScrollEffectsParams {
  parentRef: RefObject<HTMLDivElement | null>;
  cellWidthPx: number;
  dates: Dayjs[];
  visibleRowsLength: number;
  onLoadDatesLeft?: () => Promise<void>;
  onLoadDatesRight?: () => Promise<void>;
  loadDatesLeft: () => void;
  loadDatesRight: () => void;
  expandDatesLeft: () => void;
  expandDatesRight: () => void;
  setIsScrolling: (isScrolling: boolean) => void;
  normalizeExpandedDatesWindow: () => boolean;
  updateVisibleDateRange: (force?: boolean) => void;
  updateFirstVisibleDate: (force?: boolean) => void;
  startAsyncLeftFetch: (reason: string) => void;
  startAsyncRightFetch: (reason: string) => void;
  refs: ScrollEffectRefs;
}

interface ScrollEffectRefs {
  lastScrollLeftRef: RefObject<number>;
  isExtendingLeftRef: RefObject<boolean>;
  isExtendingRightRef: RefObject<boolean>;
  isLoadingLeftAsyncRef: RefObject<boolean>;
  isLoadingRightAsyncRef: RefObject<boolean>;
  pendingLeftCompensationRef: RefObject<number | null>;
  pendingRightCompensationRef: RefObject<number | null>;
  nextLeftLoadTriggerScrollLeftRef: RefObject<number | null>;
  nextRightLoadTriggerScrollLeftRef: RefObject<number | null>;
  pendingLeftAsyncFetchRef: RefObject<boolean>;
  pendingRightAsyncFetchRef: RefObject<boolean>;
  paginationSettleRafRef: RefObject<number | null>;
  lastWheelAtRef: RefObject<number>;
  programmaticHorizontalScrollUntilRef: RefObject<number>;
  isMouseButtonDownRef: RefObject<boolean>;
  lastNonWheelHorizontalScrollAtRef: RefObject<number>;
  pendingExpandedLeftInteractionScrollLeftRef: RefObject<number | null>;
  expandedWindowDuringInteractionRef: RefObject<boolean>;
  nextNonWheelLeftExpandAllowedAtRef: RefObject<number>;
  nextNonWheelRightExpandAllowedAtRef: RefObject<number>;
  lastHorizontalScrollLeftRef: RefObject<number>;
  isNearBottomRef: RefObject<boolean>;
  onLoadMoreRowsRef: RefObject<() => void>;
  hasMoreRowsRef: RefObject<boolean>;
  isLoadingMoreRowsRef: RefObject<boolean>;
  verticalPaginationSuppressedRef: RefObject<boolean>;
  scrollTimeoutRef: RefObject<number | null>;
}

export const usePlannerScrollEffects = ({
  parentRef,
  cellWidthPx,
  dates,
  visibleRowsLength,
  onLoadDatesLeft,
  onLoadDatesRight,
  loadDatesLeft,
  loadDatesRight,
  expandDatesLeft,
  expandDatesRight,
  setIsScrolling,
  normalizeExpandedDatesWindow,
  updateVisibleDateRange,
  updateFirstVisibleDate,
  startAsyncLeftFetch,
  startAsyncRightFetch,
  refs,
}: UsePlannerScrollEffectsParams): void => {
  // --- Effect 1: Prevent browser gesture navigation on horizontal swipes ---
  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    let lastPreventTime = 0;

    const preventGestureNavigation = (event: WheelEvent) => {
      refs.lastWheelAtRef.current = performance.now();

      const now = performance.now();
      if (now - lastPreventTime < WHEEL_THROTTLE_MS) return;

      const isHorizontalSwipe =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) &&
        Math.abs(event.deltaX) > HORIZONTAL_SWIPE_MIN_DELTA_PX;

      if (!isHorizontalSwipe) return;

      lastPreventTime = now;
      event.preventDefault();
    };

    container.addEventListener('wheel', preventGestureNavigation, { passive: false });
    return () => container.removeEventListener('wheel', preventGestureNavigation);
  }, [parentRef, refs]);

  // --- Effect 2: Main scroll handler (horizontal pagination + vertical lazy loading) ---
  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    let rafId = 0;

    const handleScroll = () => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        rafId = 0;

        // --- Scroll idle detection + finalize ---
        scheduleScrollIdle(container);

        // --- Horizontal scroll tracking ---
        const { scrollLeft, scrollWidth, clientWidth, scrollTop, scrollHeight, clientHeight } =
          container;
        const didHorizontalScroll = scrollLeft !== refs.lastHorizontalScrollLeftRef.current;

        if (didHorizontalScroll) {
          refs.lastHorizontalScrollLeftRef.current = scrollLeft;
          updateVisibleDateRange(false);
          updateFirstVisibleDate();
        }

        const scrollDelta = scrollLeft - refs.lastScrollLeftRef.current;
        const isScrollingLeft = scrollDelta < 0;
        const isScrollingRight = scrollDelta > 0;
        refs.lastScrollLeftRef.current = scrollLeft;

        // --- Horizontal pagination ---
        if (didHorizontalScroll) {
          handleHorizontalPagination({
            container,
            scrollLeft,
            scrollWidth,
            clientWidth,
            isScrollingLeft,
            isScrollingRight,
          });
        }

        // --- Vertical lazy loading ---
        handleVerticalPagination({
          distanceToBottom: scrollHeight - scrollTop - clientHeight,
          scrollTop,
        });
      });
    };

    /** Schedules the scroll-idle timeout and finalize logic. */
    const scheduleScrollIdle = (container: HTMLDivElement) => {
      const finalizeHorizontalInteraction = () => {
        const withinReleaseGrace =
          performance.now() - refs.lastNonWheelHorizontalScrollAtRef.current <
          NON_WHEEL_RELEASE_GRACE_MS;

        if (
          refs.expandedWindowDuringInteractionRef.current &&
          (refs.isMouseButtonDownRef.current || withinReleaseGrace)
        ) {
          refs.scrollTimeoutRef.current = window.setTimeout(
            finalizeHorizontalInteraction,
            FINALIZE_RETRY_MS
          );
          return;
        }

        if (normalizeExpandedDatesWindow()) {
          refs.scrollTimeoutRef.current = null;
          return;
        }

        updateVisibleDateRange(true);
        updateFirstVisibleDate(true);
        refs.scrollTimeoutRef.current = null;
      };

      if (!usePlannerStore.getState().isScrolling) {
        setIsScrolling(true);
      }

      if (refs.scrollTimeoutRef.current !== null) {
        clearTimeout(refs.scrollTimeoutRef.current);
      }

      refs.scrollTimeoutRef.current = window.setTimeout(() => {
        setIsScrolling(false);
        finalizeHorizontalInteraction();
      }, SCROLL_IDLE_DEBOUNCE_MS);
    };

    /**
     * Handles horizontal edge detection and triggers pagination
     * (expand/load) when near the left or right edge of the date window.
     */
    const handleHorizontalPagination = ({
      container,
      scrollLeft,
      scrollWidth,
      clientWidth,
      isScrollingLeft,
      isScrollingRight,
    }: {
      container: HTMLDivElement;
      scrollLeft: number;
      scrollWidth: number;
      clientWidth: number;
      isScrollingLeft: boolean;
      isScrollingRight: boolean;
    }) => {
      const isAnimating = container.dataset.scrollAnimating === 'true';
      const now = performance.now();
      const isProgrammatic = now < refs.programmaticHorizontalScrollUntilRef.current;
      const isDeferredInteraction =
        !isAnimating &&
        !isProgrammatic &&
        now - refs.lastWheelAtRef.current > WHEEL_COOLDOWN_MS &&
        !usePlannerStore.getState().isDragResizeInteracting;

      if (isDeferredInteraction) {
        refs.lastNonWheelHorizontalScrollAtRef.current = now;
      }

      const viewportIndices = getViewportIndices({ scrollLeft, clientWidth, cellWidthPx });
      const isNearLeftEdge = viewportIndices.startIndex < HORIZONTAL_PAGINATION_THRESHOLD_DAYS;
      const isNearRightEdge =
        viewportIndices.endIndex >= dates.length - HORIZONTAL_PAGINATION_THRESHOLD_DAYS;
      const leftDefaultTriggerPx = getDefaultLeftLoadTriggerPx(cellWidthPx);
      const rightDefaultTriggerPx = getDefaultRightLoadTriggerPx({
        datesLength: dates.length,
        cellWidthPx,
        clientWidth,
      });

      // Reset triggers when moving away from edges
      if (!isNearLeftEdge || !isScrollingLeft) {
        refs.nextLeftLoadTriggerScrollLeftRef.current = leftDefaultTriggerPx;
      }
      if (!isNearRightEdge || !isScrollingRight) {
        refs.nextRightLoadTriggerScrollLeftRef.current = rightDefaultTriggerPx;
      }

      if (isNearLeftEdge && isScrollingLeft && !isAnimating && !isProgrammatic) {
        handleLeftEdgePagination({
          scrollLeft,
          isDeferredInteraction,
          leftDefaultTriggerPx,
          viewportIndices,
        });
      } else if (
        isNearRightEdge &&
        (isScrollingRight || scrollLeft + clientWidth >= scrollWidth) &&
        !isAnimating &&
        !isProgrammatic
      ) {
        handleRightEdgePagination({
          scrollLeft,
          isDeferredInteraction,
          rightDefaultTriggerPx,
          viewportIndices,
        });
      } else if (!isAnimating && !isProgrammatic) {
        handleDataRangeEdgeFetch({
          viewportIndices,
          isScrollingLeft,
          isScrollingRight,
        });
      }
    };

    /** Triggers left-edge expand (deferred interaction) or load (wheel scroll). */
    const handleLeftEdgePagination = ({
      scrollLeft,
      isDeferredInteraction,
      leftDefaultTriggerPx,
      viewportIndices,
    }: {
      scrollLeft: number;
      isDeferredInteraction: boolean;
      leftDefaultTriggerPx: number;
      viewportIndices: { startIndex: number; endIndex: number };
    }) => {
      const leftLoadTriggerPx =
        refs.nextLeftLoadTriggerScrollLeftRef.current ?? leftDefaultTriggerPx;

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'scroll handler: near LEFT edge viewportStart=' +
          viewportIndices.startIndex +
          ' viewportEnd=' +
          viewportIndices.endIndex +
          ' dates=' +
          dates.length +
          ' loadingLeft=' +
          refs.isLoadingLeftAsyncRef.current +
          ' nonWheel=' +
          isDeferredInteraction +
          ' triggerPx=' +
          Math.round(leftLoadTriggerPx)
      );

      if (isDeferredInteraction && refs.isMouseButtonDownRef.current) {
        // Scrollbar thumb drag: do NOT modify the dates array to the left.
        // Any change — whether prepending (shifts DOM positions, browser
        // fights scroll compensation) or prepend+trim (shifts visible content
        // under the viewport) — causes visual jumps.  Instead only pre-fetch
        // data so it is ready when the interaction ends, and flag
        // normalizeExpandedDatesWindow to re-center the window on release.
        if (scrollLeft <= leftLoadTriggerPx) {
          refs.expandedWindowDuringInteractionRef.current = true;

          if (!refs.isLoadingLeftAsyncRef.current) {
            startAsyncLeftFetch('expanded-interaction LEFT');
          }
        }
      } else if (isDeferredInteraction) {
        // Non-wheel, non-scrollbar-drag interaction (e.g. touchpad momentum
        // scroll between wheel events).  Use the original expand approach.
        const canExpandNow = performance.now() >= refs.nextNonWheelLeftExpandAllowedAtRef.current;

        if (scrollLeft <= leftLoadTriggerPx && !refs.isExtendingLeftRef.current && canExpandNow) {
          refs.isExtendingLeftRef.current = true;
          refs.expandedWindowDuringInteractionRef.current = true;
          refs.nextNonWheelLeftExpandAllowedAtRef.current =
            performance.now() + LEFT_EXPAND_COOLDOWN_MS;
          refs.pendingExpandedLeftInteractionScrollLeftRef.current =
            scrollLeft + HORIZONTAL_WINDOW_SHIFT_DAYS * cellWidthPx;
          expandDatesLeft();

          if (!refs.isLoadingLeftAsyncRef.current) {
            startAsyncLeftFetch('expanded-interaction LEFT');
          }
        }
      } else if (scrollLeft <= leftLoadTriggerPx && !refs.isExtendingLeftRef.current) {
        refs.isExtendingLeftRef.current = true;
        refs.pendingLeftCompensationRef.current = scrollLeft;
        refs.pendingLeftAsyncFetchRef.current =
          Boolean(onLoadDatesLeft) && !refs.isLoadingLeftAsyncRef.current;
        loadDatesLeft();
      }
    };

    /** Triggers right-edge expand (deferred interaction) or load (wheel scroll). */
    const handleRightEdgePagination = ({
      scrollLeft,
      isDeferredInteraction,
      rightDefaultTriggerPx,
      viewportIndices,
    }: {
      scrollLeft: number;
      isDeferredInteraction: boolean;
      rightDefaultTriggerPx: number;
      viewportIndices: { startIndex: number; endIndex: number };
    }) => {
      const rightLoadTriggerPx =
        refs.nextRightLoadTriggerScrollLeftRef.current ?? rightDefaultTriggerPx;

      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'scroll handler: near RIGHT edge viewportStart=' +
          viewportIndices.startIndex +
          ' viewportEnd=' +
          viewportIndices.endIndex +
          ' dates=' +
          dates.length +
          ' loadingRight=' +
          refs.isLoadingRightAsyncRef.current +
          ' nonWheel=' +
          isDeferredInteraction +
          ' triggerPx=' +
          Math.round(rightLoadTriggerPx)
      );

      if (isDeferredInteraction) {
        const canExpandNow = performance.now() >= refs.nextNonWheelRightExpandAllowedAtRef.current;

        if (scrollLeft >= rightLoadTriggerPx && !refs.isExtendingRightRef.current && canExpandNow) {
          refs.isExtendingRightRef.current = true;
          refs.expandedWindowDuringInteractionRef.current = true;
          refs.nextNonWheelRightExpandAllowedAtRef.current =
            performance.now() + RIGHT_EXPAND_COOLDOWN_MS;
          expandDatesRight();

          if (!refs.isLoadingRightAsyncRef.current) {
            startAsyncRightFetch('expanded-interaction RIGHT');
          }
        }
      } else if (scrollLeft >= rightLoadTriggerPx && !refs.isExtendingRightRef.current) {
        refs.isExtendingRightRef.current = true;
        refs.pendingRightCompensationRef.current = scrollLeft;
        refs.pendingRightAsyncFetchRef.current =
          Boolean(onLoadDatesRight) && !refs.isLoadingRightAsyncRef.current;
        loadDatesRight();
      }
    };

    /**
     * When not near a UI window edge, check if the viewport is near the loaded
     * data range boundary and trigger an async fetch if needed.
     */
    const handleDataRangeEdgeFetch = ({
      viewportIndices,
      isScrollingLeft,
      isScrollingRight,
    }: {
      viewportIndices: { startIndex: number; endIndex: number };
      isScrollingLeft: boolean;
      isScrollingRight: boolean;
    }) => {
      const { queryDateRanges, groupBy: activeGroupBy } = usePlannerStore.getState();
      const activeRange = queryDateRanges[activeGroupBy];

      if (!activeRange || dates.length === 0) return;

      const leftDate = dates[clampDateIndex(viewportIndices.startIndex, dates.length)];
      const rightDate = dates[clampDateIndex(viewportIndices.endIndex, dates.length)];
      const leftRelation = getDateRelationToLoadedRange(leftDate, activeRange);
      const rightRelation = getDateRelationToLoadedRange(rightDate, activeRange);

      if (
        isScrollingLeft &&
        (leftRelation === DateRelation.Before || leftRelation === DateRelation.NearStart) &&
        onLoadDatesLeft &&
        !refs.isLoadingLeftAsyncRef.current
      ) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          'scroll handler: near LEFT data range edge leftDate=' +
            leftDate?.format('YYYY-MM-DD') +
            ' leftRel=' +
            leftRelation +
            ' loadingLeft=' +
            refs.isLoadingLeftAsyncRef.current
        );
        startAsyncLeftFetch('scroll handler: LEFT data range');
      } else if (
        isScrollingRight &&
        (rightRelation === DateRelation.After || rightRelation === DateRelation.NearEnd) &&
        onLoadDatesRight &&
        !refs.isLoadingRightAsyncRef.current
      ) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          'scroll handler: near RIGHT data range edge rightDate=' +
            rightDate?.format('YYYY-MM-DD') +
            ' rightRel=' +
            rightRelation +
            ' loadingRight=' +
            refs.isLoadingRightAsyncRef.current
        );
        startAsyncRightFetch('scroll handler: RIGHT data range');
      }
    };

    /** Triggers vertical row loading when near the bottom of the container. */
    const handleVerticalPagination = ({
      distanceToBottom,
      scrollTop,
    }: {
      distanceToBottom: number;
      scrollTop: number;
    }) => {
      refs.isNearBottomRef.current = distanceToBottom < LOAD_MORE_ROWS_THRESHOLD_PX;

      if (
        refs.hasMoreRowsRef.current &&
        !refs.isLoadingMoreRowsRef.current &&
        refs.isNearBottomRef.current &&
        !refs.verticalPaginationSuppressedRef.current
      ) {
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          'scroll handler: near BOTTOM distToBottom=' +
            distanceToBottom +
            ' scrollTop=' +
            Math.round(scrollTop) +
            ' visibleRows=' +
            visibleRowsLength
        );

        refs.isLoadingMoreRowsRef.current = true;
        refs.onLoadMoreRowsRef.current();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);

      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      if (refs.scrollTimeoutRef.current !== null) {
        clearTimeout(refs.scrollTimeoutRef.current);
        refs.scrollTimeoutRef.current = null;
      }
    };
  }, [
    cellWidthPx,
    dates,
    expandDatesLeft,
    expandDatesRight,
    loadDatesLeft,
    loadDatesRight,
    normalizeExpandedDatesWindow,
    onLoadDatesLeft,
    onLoadDatesRight,
    parentRef,
    refs,
    setIsScrolling,
    startAsyncLeftFetch,
    startAsyncRightFetch,
    updateFirstVisibleDate,
    updateVisibleDateRange,
    visibleRowsLength,
  ]);
};
